import React from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useTheme } from "../../context/ThemeContext";
import TransliteratedInput from "../common/TransliteratedInput";
import AutoSave from "../common/AutoSave";

const PatientTapasanyaForm = (props) => {
  const { theme, isDarkMode } = useTheme();
  const { initialValues, onSubmit, onBack, isFirstStep, isLastStep, onAutoSave } = props;

  const patientTapasanyaInfo = initialValues.patientTapasanya?.[0] || {};

  const formInitialValues = {
    VISIT_DATE: patientTapasanyaInfo?.VISIT_DATE ? patientTapasanyaInfo.VISIT_DATE.substring(0, 10) : "",
    PATIENT_TAPASANYA: patientTapasanyaInfo?.PATIENT_TAPASANYA || "",
    PATIENT_WEIGHT: patientTapasanyaInfo?.PATIENT_WEIGHT || "",
    PATIENT_BP: patientTapasanyaInfo?.PATIENT_BP || "",
    PATIENT_PULSERATE: patientTapasanyaInfo?.PATIENT_PULSERATE || 0,
  };

  const validationSchema = Yup.object().shape({
    VISIT_DATE: Yup.date().nullable(),
    PATIENT_WEIGHT: Yup.string(),
    PATIENT_BP: Yup.string(),
    PATIENT_PULSERATE: Yup.number().integer().nullable(),
  });

  return (
    <Formik
      initialValues={formInitialValues}
      validationSchema={validationSchema}
      onSubmit={(values, { setSubmitting }) => {
        onSubmit(values);
        setSubmitting(false);
      }}
      enableReinitialize={true}
    >
      {({ errors, touched, isSubmitting }) => (
        <Form className="flex flex-col flex-1 space-y-4 md:space-y-6 w-full">
          <AutoSave onAutoSave={(values) => (onAutoSave || onSubmit)?.(values, { isAutoSave: true })} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <div className="space-y-2">
              <label
                htmlFor="VISIT_DATE"
                className="block text-sm font-medium transition-colors"
                style={{ color: theme.text }}
              >
                Visit Date
              </label>
              <Field
                name="VISIT_DATE"
                type="date"
                className={`w-full px-4 py-2.5 rounded-lg border transition-all duration-200 focus:outline-none focus:ring-2 ${touched.VISIT_DATE && errors.VISIT_DATE
                  ? 'border-red-500 focus:ring-red-500'
                  : 'focus:ring-opacity-50'
                  }`}
                style={{
                  backgroundColor: theme.inputBg,
                  borderColor: touched.VISIT_DATE && errors.VISIT_DATE ? '#d63031' : theme.border,
                  color: theme.text,
                }}
              />
              <ErrorMessage name="VISIT_DATE" component="div" className="text-red-500 text-sm mt-1" />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="PATIENT_WEIGHT"
                className="block text-sm font-medium transition-colors"
                style={{ color: theme.text }}
              >
                Weight
              </label>
              <Field
                name="PATIENT_WEIGHT"
                type="text"
                className={`w-full px-4 py-2.5 rounded-lg border transition-all duration-200 focus:outline-none focus:ring-2 ${touched.PATIENT_WEIGHT && errors.PATIENT_WEIGHT
                  ? 'border-red-500 focus:ring-red-500'
                  : 'focus:ring-opacity-50'
                  }`}
                style={{
                  backgroundColor: theme.inputBg,
                  borderColor: touched.PATIENT_WEIGHT && errors.PATIENT_WEIGHT ? '#d63031' : theme.border,
                  color: theme.text,
                }}
              />
              <ErrorMessage name="PATIENT_WEIGHT" component="div" className="text-red-500 text-sm mt-1" />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="PATIENT_BP"
                className="block text-sm font-medium transition-colors"
                style={{ color: theme.text }}
              >
                Blood Pressure
              </label>
              <Field
                name="PATIENT_BP"
                type="text"
                className={`w-full px-4 py-2.5 rounded-lg border transition-all duration-200 focus:outline-none focus:ring-2 ${touched.PATIENT_BP && errors.PATIENT_BP
                  ? 'border-red-500 focus:ring-red-500'
                  : 'focus:ring-opacity-50'
                  }`}
                style={{
                  backgroundColor: theme.inputBg,
                  borderColor: touched.PATIENT_BP && errors.PATIENT_BP ? '#d63031' : theme.border,
                  color: theme.text,
                }}
              />
              <ErrorMessage name="PATIENT_BP" component="div" className="text-red-500 text-sm mt-1" />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="PATIENT_PULSERATE"
                className="block text-sm font-medium transition-colors"
                style={{ color: theme.text }}
              >
                Pulse Rate
              </label>
              <Field
                name="PATIENT_PULSERATE"
                type="number"
                className={`w-full px-4 py-2.5 rounded-lg border transition-all duration-200 focus:outline-none focus:ring-2 ${touched.PATIENT_PULSERATE && errors.PATIENT_PULSERATE
                  ? 'border-red-500 focus:ring-red-500'
                  : 'focus:ring-opacity-50'
                  }`}
                style={{
                  backgroundColor: theme.inputBg,
                  borderColor: touched.PATIENT_PULSERATE && errors.PATIENT_PULSERATE ? '#d63031' : theme.border,
                  color: theme.text,
                }}
              />
              <ErrorMessage name="PATIENT_PULSERATE" component="div" className="text-red-500 text-sm mt-1" />
            </div>
          </div>

          <div className="space-y-2">
            <label
              htmlFor="PATIENT_TAPASANYA"
              className="block text-sm font-medium transition-colors"
              style={{ color: theme.text }}
            >
              Tapasanya
            </label>
            <Field name="PATIENT_TAPASANYA">
              {({ field, form }) => (
                <TransliteratedInput
                  {...field}
                  onChangeText={(text) => form.setFieldValue(field.name, text)}
                  className="w-full px-4 py-2.5 rounded-lg border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-opacity-50"
                  style={{
                    backgroundColor: theme.inputBg,
                    borderColor: theme.border,
                    color: theme.text,
                  }}
                  as="textarea"
                  rows={6}
                  placeholder="Enter tapasanya details..."
                />
              )}
            </Field>
            <ErrorMessage name="PATIENT_TAPASANYA" component="div" className="text-red-500 text-sm mt-1" />
          </div>

          <div
            className="flex justify-between items-center pt-2 mt-4 border-t sticky bottom-0 z-[100] py-3 -mx-4 md:-mx-10 lg:-mx-12 px-4 md:px-10 lg:px-12"
            style={{
              borderColor: theme.border,
              backgroundColor: theme.card,
              boxShadow: '0 -4px 10px rgba(0,0,0,0.05)'
            }}
          >
            {!isFirstStep && onBack && (
              <button
                type="button"
                onClick={onBack}
                className="px-4 py-1.5 rounded-full font-semibold transition-all duration-200 hover:scale-105 active:scale-95 text-xs md:text-sm"
                style={{
                  backgroundColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                  color: theme.text,
                  border: `1px solid ${theme.border}`
                }}
              >
                Back
              </button>
            )}
            <div className={isFirstStep ? "ml-auto" : ""}>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2 rounded-full font-bold text-white transition-all duration-200 hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed text-sm md:text-base shadow-lg animate-flow"
                style={{
                  backgroundImage: `linear-gradient(to right, ${theme.brownTwo}, ${theme.secondary}, ${theme.primary}, ${theme.brownTwo})`,
                }}
              >
                {isLastStep ? "Submit" : "Next"}
              </button>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default PatientTapasanyaForm;
